import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { fmtDate } from '@/lib/dates';
import { CheckCircle2, Circle, GraduationCap, AlertTriangle, Search, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMING = [
  { title: 'Course & Module Catalog', desc: 'Define Service Ready courses, modules and TFS / Spartan CleanCheck materials in one managed catalog.' },
  { title: 'Bulk Assignment', desc: 'Assign a course to many people or a whole role/location at once, with due dates.' },
  { title: 'Certification Tracking', desc: 'Track required certifications, expirations and renewals across the workforce.' },
];

const StatusDot = ({ done }) => (
  done
    ? <CheckCircle2 size={16} className="text-emerald-600" />
    : <Circle size={16} className="text-slate-300" />
);

const OpsTraining = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/personnel/training/roster`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setData(r.data); setErr(false); })
      .catch(() => { setErr(true); setData({ items: [], personnel: [], people: 0, completed_totals: {} }); });
  }, [token]);

  if (!data) return <div className="p-10 text-center text-slate-500" data-testid="ops-training-loading">Loading training records…</div>;
  if (err) return (
    <div className="mx-auto max-w-md rounded-xl border border-rose-200 bg-rose-50 p-8 text-center" data-testid="ops-training-error">
      <AlertTriangle className="mx-auto mb-2 text-rose-500" size={26} />
      <div className="text-sm font-semibold text-rose-700">Training records could not be loaded.</div>
      <div className="mt-1 text-xs text-slate-500">Please refresh the page or check your access.</div>
    </div>
  );

  const items = data.items || [];
  const rows = (data.personnel || []).filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (p.name || '').toLowerCase().includes(s) || String(p.personnel_number || '').includes(s);
  });
  const fullyDone = (data.personnel || []).filter((p) => p.outstanding === 0).length;

  return (
    <div className="space-y-6" data-testid="ops-training">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-[#0E2A47] p-2.5 text-white"><GraduationCap size={22} /></div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0E2A47]">Training</h1>
          <p className="text-sm text-slate-500">Service Ready by TFS — company-wide training completion across the authoritative Personnel Master.</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-training-stat-people">
          <div className="text-2xl font-extrabold text-[#0E2A47]">{data.people}</div>
          <div className="text-xs font-semibold text-slate-500">Active personnel</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-training-stat-complete">
          <div className="text-2xl font-extrabold text-emerald-600">{fullyDone}</div>
          <div className="text-xs font-semibold text-slate-500">All items complete</div>
        </div>
        {items.slice(0, 2).map((it) => (
          <div key={it.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ops-training-stat-${it.key}`}>
            <div className="text-2xl font-extrabold text-[#0E2A47]">{(data.completed_totals || {})[it.key] || 0}</div>
            <div className="text-xs font-semibold text-slate-500">{it.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search personnel…"
          data-testid="ops-training-search"
          className="w-full rounded-full border border-slate-200 py-2 pl-9 pr-4 text-sm" />
      </div>

      {/* Roster table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="ops-training-table-wrap">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Personnel</th>
              {items.map((it) => <th key={it.key} className="px-3 py-3 text-center">{it.label}</th>)}
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.personnel_id} className="border-b border-slate-100 last:border-0" data-testid={`ops-training-row-${p.personnel_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0E2A47]">{p.name}</div>
                  <div className="text-xs text-slate-400">#{p.personnel_number || '—'}{p.worker_classification ? ` · ${p.worker_classification}` : ''}</div>
                </td>
                {items.map((it) => {
                  const rec = (p.items || {})[it.key] || {};
                  return (
                    <td key={it.key} className="px-3 py-3 text-center" title={rec.completion_date ? fmtDate(rec.completion_date) : ''}>
                      <div className="flex flex-col items-center gap-0.5">
                        <StatusDot done={rec.completed} />
                        {rec.completed && rec.completion_date && <span className="text-[10px] text-slate-400">{fmtDate(rec.completion_date)}</span>}
                      </div>
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-center">
                  {p.outstanding === 0
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">Complete</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700"><Clock size={11} />{p.outstanding} outstanding</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={items.length + 2} className="px-4 py-10 text-center text-slate-400">No personnel match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Records are read from the single authoritative Personnel Master. To update a completion, open the person's profile in Personnel. The matching training then appears for that individual in TEAM.</p>

      {/* Not-yet-implemented management capabilities — clearly flagged, not represented as done */}
      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4" data-testid="ops-training-coming">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-amber-800"><AlertTriangle size={16} />Training management — planned, not yet built</div>
        <div className="grid gap-3 sm:grid-cols-3">
          {COMING.map((c) => (
            <div key={c.title} className="rounded-lg border border-amber-200 bg-white p-3">
              <div className="text-sm font-bold text-[#0E2A47]">{c.title}</div>
              <div className="mt-1 text-xs text-slate-500">{c.desc}</div>
              <div className="mt-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Coming soon</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OpsTraining;
